import {
  normalizePartnerCode,
  PARTNER_ATTRIBUTION_DAYS,
  PARTNER_QUERY_PARAM,
} from '@pulsify/shared/dto'
import { storageManager } from '~/shared/lib/storage-manager'

const STORAGE_KEY = 'pulsify.partner'
const COOKIE_NAME = 'pulsify_partner'
const DAY_MS = 86_400_000
const WINDOW_MS = PARTNER_ATTRIBUTION_DAYS * DAY_MS

interface StoredPartner {
  code: string
  at: number
}

function parse(value: string | null | undefined): StoredPartner | null {
  if (!value) return null
  const [rawCode, rawAt] = value.split(':')
  const code = normalizePartnerCode(rawCode)
  const at = Number(rawAt)
  return code && Number.isFinite(at) ? { code, at } : null
}

function fresh(entry: StoredPartner | null, now: number): StoredPartner | null {
  return entry && now - entry.at < WINDOW_MS && entry.at <= now ? entry : null
}

function readLocal(): StoredPartner | null {
  const value = storageManager.get<unknown>(STORAGE_KEY)
  return typeof value === 'string' ? parse(value) : null
}

function readCookie(): StoredPartner | null {
  try {
    const match = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${COOKIE_NAME}=`))
    return parse(match ? decodeURIComponent(match.slice(COOKIE_NAME.length + 1)) : null)
  } catch {
    return null
  }
}

function writeCookie(value: string): boolean {
  try {
    const secure = location.protocol === 'https:' ? '; Secure' : ''
    // biome-ignore lint/suspicious/noDocumentCookie: Safari has no Cookie Store API
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; Max-Age=${WINDOW_MS / 1000}; Path=/; SameSite=Lax${secure}`
    return true
  } catch {
    return false
  }
}

function write(entry: StoredPartner) {
  const value = `${entry.code}:${entry.at}`
  storageManager.set(STORAGE_KEY, value)
  writeCookie(value)
}

/** The partner code this browser first arrived with, while it is less than 60 days old. */
export function storedPartnerCode(now = Date.now()): string | null {
  return (fresh(readLocal(), now) ?? fresh(readCookie(), now))?.code ?? null
}

/** Keeps `?partner=` from the landing URL unless an earlier, still valid partner is stored. */
export function capturePartnerFromUrl(search: string, now = Date.now()): void {
  let code: string | null
  try {
    code = normalizePartnerCode(new URLSearchParams(search).get(PARTNER_QUERY_PARAM))
  } catch {
    return
  }
  if (!code || storedPartnerCode(now)) return
  write({ code, at: now })
}
